// utils/supabase.ts
import { createClient, PostgrestError, SupportedStorage } from '@supabase/supabase-js';
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import * as WebBrowser from 'expo-web-browser';
import * as Linking from 'expo-linking';
import { secureStorage } from './secureStorage';

// ✅ FIX: run only on real web client (prevents SSR crash -> React #419)
if (Platform.OS === 'web' && typeof window !== 'undefined') {
  WebBrowser.maybeCompleteAuthSession();
}

const supabaseUrl =
  process.env.EXPO_PUBLIC_SUPABASE_URL ||
  Constants.expoConfig?.extra?.EXPO_PUBLIC_SUPABASE_URL;

const supabaseAnonKey =
  process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ||
  Constants.expoConfig?.extra?.EXPO_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  // Keep this as error (useful), but don’t dump full values
  console.error('Missing Supabase environment variables', {
    hasUrl: !!supabaseUrl,
    hasAnonKey: !!supabaseAnonKey,
  });
  throw new Error('Missing Supabase environment variables');
}

/**
 * Redirect URL
 * - Web: use current origin (works for localhost / staging / www / non-www)
 * - Native: use Linking.createURL
 */
const getRedirectUrl = (): string => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return `${window.location.origin}/auth/callback`;
  }
  return Linking.createURL('/auth/callback');
};

const redirectUrl = getRedirectUrl();


const AUTH_DEBUG_PREFIX = '[AuthFlow]';
const logAuthDebug = (event: string, details?: Record<string, unknown>) => {
  if (__DEV__) {
    console.log(`${AUTH_DEBUG_PREFIX} ${event}`, details ?? {});
  }
};

const getPkceLocalStorageState = (): { keyCount: number; keys: string[] } => {
  if (Platform.OS !== 'web' || typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return { keyCount: 0, keys: [] };
  }

  const keys: string[] = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (!key) continue;
    if (key.toLowerCase().includes('pkce') || key.toLowerCase().includes('code-verifier')) {
      keys.push(key);
    }
  }

  return { keyCount: keys.length, keys };
};

/**
 * Storage for Supabase auth
 * - Native: SecureStore via our wrapper
 * - Web: allow Supabase to handle session-in-url (PKCE) + its default handling
 */
const supabaseSecureStorage: SupportedStorage =
  Platform.OS === 'web'
    ? {
        getItem: (key: string) => {
          if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return null;
          return window.localStorage.getItem(key);
        },
        setItem: (key: string, value: string) => {
          if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return;
          window.localStorage.setItem(key, value);
        },
        removeItem: (key: string) => {
          if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return;
          window.localStorage.removeItem(key);
        },
      }
    : {
        getItem: async (key: string) => await secureStorage.getItem(key),
        setItem: async (key: string, value: string) => await secureStorage.setItem(key, value),
        removeItem: async (key: string) => await secureStorage.removeItem(key),
      };

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: supabaseSecureStorage,
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false,
    flowType: 'pkce',
  },
});

export class SupabaseQuery {
  static async execute<T>(
    queryFn: () => Promise<{ data: T | null; error: PostgrestError | null }>
  ): Promise<T | null> {
    try {
      const { data, error } = await queryFn();

      if (error) {
        console.error('Supabase Query Error:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });
        throw new Error(error.message);
      }

      return data;
    } catch (error) {
      console.error('Query execution failed:', error);
      throw error;
    }
  }
}

export const withTimeout = <T>(promise: Promise<T>, timeoutMs: number = 10000): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
    ),
  ]);
};

class RateLimiter {
  private requests: number[] = [];
  private maxRequests = 60;

  async checkLimit(): Promise<boolean> {
    const now = Date.now();
    this.requests = this.requests.filter((time) => now - time < 60000);
    if (this.requests.length >= this.maxRequests) return false;
    this.requests.push(now);
    return true;
  }

  async waitForSlot(): Promise<void> {
    while (!(await this.checkLimit())) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }
}

export const rateLimiter = new RateLimiter();

export const signInWithGoogle = async (): Promise<{ success: boolean; error?: any }> => {
  try {
    logAuthDebug('before_signInWithOAuth', {
      origin: Platform.OS === 'web' && typeof window !== 'undefined' ? window.location.origin : null,
      redirectUrl,
      pkce: getPkceLocalStorageState(),
    });

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: redirectUrl, skipBrowserRedirect: false },
    });

    if (error) {
      console.error('Supabase OAuth error:', error);
      logAuthDebug('signInWithOAuth_failure', {
        message: error.message,
        name: (error as Error)?.name,
      });
      return { success: false, error };
    }

    logAuthDebug('signInWithOAuth_success', { redirectUrl });
    return { success: true };
  } catch (error) {
    console.error('Google sign in error:', error);
    logAuthDebug('signInWithOAuth_exception', {
      message: (error as Error)?.message,
      name: (error as Error)?.name,
    });
    return { success: false, error };
  }
};

export const checkAdminStatus = async (userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('admins')
      .select('user_id, is_active')
      .eq('user_id', userId)
      .eq('is_active', true)
      .maybeSingle();

    if (error) {
      console.error('Error checking admin status:', error);
      return false;
    }

    return !!data;
  } catch (e) {
    console.error('Exception checking admin:', e);
    return false;
  }
};

export const getCurrentAdmin = async () => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user?.id) return null;

    const { data, error } = await supabase
      .from('admins')
      .select('id, full_name, role, last_login_at, is_active, user_id')
      .eq('user_id', session.user.id)
      .eq('is_active', true)
      .single();

    if (error) {
      console.error('Error getting admin info:', error);
      return null;
    }

    return { ...data, email: session.user.email || null };
  } catch (e) {
    console.error('Exception getting admin:', e);
    return null;
  }
};